import { useToast } from '@teachino/shared-design-system';
import { $t } from '@teachino/shared-i18n';
import {
  type FragmentOf,
  graphql,
  readFragment,
} from '@teachino/shared-infrastructure';
import { useMutation, useQuery } from '@urql/vue';
import { computed } from 'vue';
import { featureFragment } from './feature.fragment';
import { personalLicenseFragment } from './personal-license.fragment';
import {
  type BillingCycle,
  requestCheckoutSessionDataMutation,
} from './request-checkout-session-data.mutation';

const upsellUrl = 'https://www.teachino.io/in-app-schullizenz';

const licenseFragment = graphql(`
  fragment License on License @_unmask {
    id
    holderType
    plan {
      id
      type
      features {
        key
      }
    }
  }
`);
export type License = FragmentOf<typeof licenseFragment>;

export function useLicense() {
  const toast = useToast();
  const licenseQuery = graphql(
    `
      query Licenses {
        licenses {
          ...License
          ...PersonalLicense
        }
      }
    `,
    [licenseFragment, personalLicenseFragment],
  );

  const licenseQueryResult = useQuery({ query: licenseQuery });

  const requestCheckoutSessionData = useMutation(
    requestCheckoutSessionDataMutation,
  );
  const requestPaymentGatewayPortalDataMutation = useMutation(
    graphql(`
      mutation RequestPaymentGatewayPortalData(
        $paymentGatewayPortalInput: PaymentGatewayPortalInput!
      ) {
        requestPaymentGatewayPortalData(
          paymentGatewayPortalInput: $paymentGatewayPortalInput
        ) {
          url
          gateway
        }
      }
    `),
  );

  const allUserLicenses = computed<License[]>(
    () => licenseQueryResult.data.value?.licenses ?? [],
  );

  const hasPaidPlan = computed<boolean>(() => {
    return allUserLicenses.value.some(
      (license) =>
        license.plan.type === 'PAID' || license.plan.type === 'FRIENDS',
    );
  });

  async function openProLicenseCheckout(args: {
    discountId?: string;
    billingCycle: BillingCycle;
  }) {
    const { discountId, billingCycle } = args;
    const { data, error } = await requestCheckoutSessionData.executeMutation({
      licensingSessionCheckoutInput: {
        planKey: 'teacher_pro',
        billingCycle,
        discountId,
      },
    });
    if (error) {
      toast.error($t('generic_error'));
      return;
    }
    const url = data?.requestCheckoutSessionData.url;
    if (url) {
      openUrlInNewTab(url);
    }
  }

  function openUrlInNewTab(url: string): void {
    const newTabOpened = window.open(url, '_blank');
    if (!newTabOpened) {
      window.location.href = url;
    }
  }

  async function startManageSubscription(licenseId: string) {
    const { data, error } =
      await requestPaymentGatewayPortalDataMutation.executeMutation({
        paymentGatewayPortalInput: {
          licenseId,
        },
      });
    if (error) {
      toast.error();
      return;
    }
    const url = data?.requestPaymentGatewayPortalData.url;
    if (url) {
      openUrlInNewTab(url);
    }
  }

  function openSchoolLicense() {
    openUrlInNewTab(upsellUrl);
  }

  function highlightsForFeatures(
    features: FragmentOf<typeof featureFragment>[],
  ): string[] {
    const unwrappedFeatures = features.map((feature) =>
      readFragment(featureFragment, feature),
    );
    // we don't want to show all jfeatures - here we control what we show

    const featureKeyDescriptionMap: Record<string, string> = {
      /* eslint-disable @typescript-eslint/naming-convention */
      integration_timetable: $t('upgrade.feature.integration_timetable'),
      calendar_personal_events: $t('upgrade.feature.daily_changes'),
      calendar_vacation: $t('upgrade.feature.daily_changes'),
      calendar_additional_periods: $t('upgrade.feature.daily_changes'),
      student_access: $t('upgrade.feature.student_access'),
      collaboration_substitutions: $t(
        'upgrade.feature.collaboration_substitutions',
      ),
      collaboration_course_templates: $t(
        'upgrade.feature.collaboration_course_templates',
      ),
      collaboration_teamteaching: $t(
        'upgrade.feature.collaboration_teamteaching',
      ),
      ai_basic: $t('upgrade.feature.ai_basic'),
      ai_advanced: $t('upgrade.feature.ai_advanced'),
      presentation_mode: $t('upgrade.feature.presentation'),
    };

    /* eslint-enable @typescript-eslint/naming-convention */

    const featureDescriptions = unwrappedFeatures.flatMap(
      (feature) => featureKeyDescriptionMap[feature.key] ?? [],
    );

    const uniqueFeatureDescriptions = Array.from(new Set(featureDescriptions));

    const hasUnlimitedFiles = unwrappedFeatures.some(
      (feature) => feature.key === 'files_unlimited',
    );

    return [
      ...uniqueFeatureDescriptions,
      ...(hasUnlimitedFiles
        ? [$t('upgrade.feature.files_unlimited')]
        : [$t('upgrade.feature.files_restricted')]),
    ];
  }

  const personalLicenses = computed<License[]>(() => {
    return allUserLicenses.value.filter(
      (license) => license.holderType === 'USER',
    );
  });

  const isInTrial = computed(() => {
    if (hasPaidPlan.value) {
      return false;
    }
    return allUserLicenses.value.some(
      (license) => license.plan.type === 'TRIAL',
    );
  });

  return {
    hasPaidPlan,
    openProLicenseCheckout,
    openSchoolLicense,
    startManageSubscription,
    highlightsForFeatures,
    personalLicenses,
    isInTrial,
  };
}
